import React, { useState, useEffect, useMemo } from 'react';
import axios from '../../axios';
import classes from './Account.module.css';

const Account = ({ accountData: initialAccountData, onAccountUpdate }) => {
  const [accountData, setAccountData] = useState(initialAccountData || {
    firstName: '',
    lastName: '',
    email: '',
    twoFactorEnabled: false,
    accountNumber: ''
  });
  const [loading, setLoading] = useState(!initialAccountData);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [toggling2FA, setToggling2FA] = useState(false);

  const isDataChanged = useMemo(() => {
    if (!initialAccountData) return false;
    return accountData.firstName !== initialAccountData.firstName ||
      accountData.lastName !== initialAccountData.lastName
  }, [accountData, initialAccountData]);

  useEffect(() => {
    if (initialAccountData) {
      setAccountData({
        id: initialAccountData.id || null,
        firstName: initialAccountData.firstName || '',
        lastName: initialAccountData.lastName || '',
        email: initialAccountData.email || '',
        twoFactorEnabled: initialAccountData.twoFactorEnabled || false,
        accountNumber: initialAccountData.accountNumber || ''
      });
      setLoading(false);
    }
  }, [initialAccountData]);

  const handleInputChange = (field, value) => {
    setAccountData(prev => ({
      ...prev,
      [field]: value
    }));
    setSuccessMessage(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const response = await axios.put('/account', {
        firstName: accountData.firstName,
        lastName: accountData.lastName,
        email: accountData.email // Pass current email (not changing it, but required for validation)
      });

      const updatedData = {
        id: response.data.id || accountData.id || null,
        firstName: response.data.firstName || '',
        lastName: response.data.lastName || '',
        email: response.data.email || '',
        twoFactorEnabled: response.data.twoFactorEnabled || false,
        accountNumber: response.data.accountNumber || ''
      };
      setAccountData(updatedData);
      
      // Update parent component
      if (onAccountUpdate) {
        onAccountUpdate(response.data);
      }
      
      setSuccessMessage('Account updated successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle2FA = async () => {
    try {
      setToggling2FA(true);
      setError(null);
      setSuccessMessage(null);

      // Get account ID from accountData
      const accountId = accountData.id;
      
      if (!accountId) {
        throw new Error('Account ID not available. Please refresh the page.');
      }

      const response = await axios.post(`/account/${accountId}/toggle-2fa`);

      // Show the shared secret in an alert when enabling
      if (response.data.secret && !accountData.twoFactorEnabled) {
        alert(`Your 2FA shared secret is:\n\n${response.data.secret}\n\nPlease save this secret in your authenticator app. You will not be able to see it again.`);
      }

      const updatedData = {
        ...accountData,
        twoFactorEnabled: response.data.account?.twoFactorEnabled !== undefined 
          ? response.data.account.twoFactorEnabled 
          : response.data.twoFactorEnabled !== undefined
          ? response.data.twoFactorEnabled
          : !accountData.twoFactorEnabled
      };
      setAccountData(updatedData);
      
      // Update parent component
      if (onAccountUpdate) {
        onAccountUpdate({
          ...accountData,
          twoFactorEnabled: updatedData.twoFactorEnabled
        });
      }
      
      setSuccessMessage(
        updatedData.twoFactorEnabled 
          ? 'Two-factor authentication enabled successfully'
          : 'Two-factor authentication disabled successfully'
      );
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to toggle two-factor authentication');
    } finally {
      setToggling2FA(false);
    }
  };

  if (loading) {
    return (
      <div className={classes.loading}>
        <span>Loading...</span>
      </div>
    );
  }

  return (
    <div className={classes.accountForm}>
      {error && <div className={classes.error}>{error}</div>}
      {successMessage && <div className={classes.success}>{successMessage}</div>}
      
      <div className={classes.formRow}>
        <label className={classes.label}>First name</label>
        <input
          type="text"
          className={classes.input}
          value={accountData.firstName}
          onChange={(e) => handleInputChange('firstName', e.target.value)}
        />
      </div>

      <div className={classes.formRow}>
        <label className={classes.label}>Last name</label>
        <input
          type="text"
          className={classes.input}
          value={accountData.lastName}
          onChange={(e) => handleInputChange('lastName', e.target.value)}
        />
      </div>

      <div className={classes.formRow}>
        <label className={classes.label}>E-mail</label>
        <input
          type="email"
          className={classes.inputReadonly}
          value={accountData.email}
          readOnly
          title="Email change requires verification"
        />
      </div>

      <div className={classes.formRow}>
        <label className={classes.label}>Two factor auth</label>
        <span className={classes.linkValue}>
          <button 
            className={classes.enableLink}
            onClick={handleToggle2FA}
            disabled={toggling2FA}
          >
            {toggling2FA 
              ? (accountData.twoFactorEnabled ? 'Disabling...' : 'Enabling...')
              : (accountData.twoFactorEnabled ? 'Disable' : 'Enable')
            }
          </button>
        </span>
      </div>

      <div className={classes.formRow}>
        <label className={classes.label}>Account number</label>
        <span className={classes.value}>{accountData.accountNumber || '—'}</span>
      </div>

      <div className={classes.buttonRow}>
        <button 
          className={classes.saveButton}
          onClick={handleSave}
          disabled={saving || !isDataChanged}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

export default Account;

